// rc128 — Ajustes rediseñado: PORTADA de tarjetas agrupadas por área con
// estado vivo + buscador. Cada tarjeta abre su propia página
// (/ajustes/<slug>, ver AjustesSectionPage). Antes: 11 pestañas en fila.
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { GRUPOS, HASH_A_SECCION, SECCIONES, type AjustesSeccion } from "./ajustesCatalog";
import { useAjustesStatus, type Chip } from "./useAjustesStatus";
import { useAuthStore } from "@/lib/auth";

const TONE: Record<NonNullable<Chip["tone"]>, string> = {
  ok: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  warn: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
  bad: "bg-destructive/10 text-destructive border-destructive/20",
  muted: "bg-muted text-muted-foreground border-border",
};

function ChipPill({ chip }: { chip: Chip }) {
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium leading-none ${TONE[chip.tone ?? "muted"]}`}>
      {chip.label}
    </span>
  );
}

function SeccionCard({ s, chips }: { s: AjustesSeccion; chips?: Chip[] }) {
  const Icon = s.icon;
  return (
    <Link
      to={`/ajustes/${s.slug}`}
      className="group flex items-start gap-3 rounded-lg border bg-card p-4 text-left transition-colors hover:border-primary/50 hover:bg-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-sm font-semibold">{s.titulo}</h3>
          <ChevronRight size={14} className="ml-auto shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </div>
        <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{s.descripcion}</p>
        {chips && chips.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {chips.map((c, i) => <ChipPill key={i} chip={c} />)}
          </div>
        )}
      </div>
    </Link>
  );
}

export function AjustesPage() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [q, setQ] = useState("");
  const status = useAjustesStatus();

  // Deep-links legacy: /ajustes#auto-facturacion → /ajustes/facturacion#auto-facturacion
  useEffect(() => {
    if (!hash) return;
    const h = hash.replace(/^#/, "");
    const slug = HASH_A_SECCION[h];
    if (slug) navigate(`/ajustes/${slug}#${h}`, { replace: true });
  }, [hash, navigate]);

  const hasPermission = useAuthStore((st) => st.hasPermission);
  const visibles = useMemo(
    () => SECCIONES.filter((s) => !s.perm || hasPermission(s.perm)),
    [hasPermission],
  );

  const filtradas = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return visibles;
    return visibles.filter((s) =>
      s.titulo.toLowerCase().includes(t) ||
      s.descripcion.toLowerCase().includes(t) ||
      (s.keywords ?? []).some((k) => k.toLowerCase().includes(t)),
    );
  }, [q, visibles]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Ajustes</h2>
          <p className="text-xs text-muted-foreground">
            Configuración del sistema. Los cambios aplican inmediatamente y quedan en auditoría.
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar en ajustes… (toku, promesas, vpn)"
            className="pl-8"
            aria-label="Buscar en ajustes"
          />
        </div>
      </div>

      {filtradas.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center text-xs text-muted-foreground">
          Nada coincide con “{q}”.
        </div>
      )}

      {GRUPOS.map((g) => {
        const items = filtradas.filter((s) => s.grupo === g.id);
        if (items.length === 0) return null;
        return (
          <section key={g.id} className="flex flex-col gap-2">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.titulo}</h3>
              <p className="text-[11px] text-muted-foreground/80">{g.descripcion}</p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((s) => <SeccionCard key={s.slug} s={s} chips={status[s.slug]} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}
